// The calendar's own date arithmetic: which days a view covers, and how moving back and
// forth works in each of the three ranges.
//
// ITS OWN MODULE rather than an addition to utils/dates.js. Nothing outside this screen
// asks "which days are in the week of the 4th", and utils/dates.js is the shared file
// every other feature imports — keeping a screen's arithmetic in the screen's folder is
// the same placement rule the pages follow.
//
// STRINGS THROUGHOUT, exactly as utils/dates.js does it: 'YYYY-MM-DD' in, 'YYYY-MM-DD'
// out, and the one Date object each helper builds is at UTC midnight so no timezone can
// turn a whole number of days into a different day.
//
// THE WEEK STARTS ON SUNDAY. That is the working week here, not a locale guess — the
// day-name list in i18n/strings/calendar.js is in the same order and the two must agree.
import { addDays } from '../../utils/dates';

const pad = (n) => String(n).padStart(2, '0');

// Day of the week for a 'YYYY-MM-DD' string: 0 = Sunday.
export const weekdayOf = (date) => {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
};

export const startOfWeek = (date) => addDays(date, -weekdayOf(date));

export const startOfMonth = (date) => `${date.slice(0, 7)}-01`;

export const endOfMonth = (date) => {
  const [y, m] = date.split('-').map(Number);
  // Day 0 of the next month is the last day of this one — no month-length table, and
  // February is right in a leap year for free.
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${date.slice(0, 7)}-${pad(last)}`;
};

// n consecutive days from `start`.
export const daysFrom = (start, n) => Array.from({ length: n }, (_, i) => addDays(start, i));

// THE WINDOW A VIEW FETCHES. The month view deliberately asks for MORE than the month:
// its grid draws whole weeks, so the leading and trailing cells belong to the neighbouring
// months and would otherwise render empty while actually holding meetings.
export const rangeFor = (view, anchor) => {
  if (view === 'day') return { from: anchor, to: anchor };
  if (view === 'week') {
    const from = startOfWeek(anchor);
    return { from, to: addDays(from, 6) };
  }
  const from = startOfWeek(startOfMonth(anchor));
  return { from, to: addDays(from, 41) }; // six weeks always covers a month
};

// Moving back and forth means a different step in each view, and the step is the view's
// own unit — a month view that paged by a week would be unreadable.
export const shiftAnchor = (view, anchor, delta) => {
  if (view === 'day') return addDays(anchor, delta);
  if (view === 'week') return addDays(anchor, delta * 7);
  const [y, m] = anchor.split('-').map(Number);
  const total = y * 12 + (m - 1) + delta;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  // Clamp the day, so paging from the 31st never skips a 30-day month.
  const last = new Date(Date.UTC(ny, nm, 0)).getUTCDate();
  const day = Math.min(Number(anchor.slice(8, 10)), last);
  return `${ny}-${pad(nm)}-${pad(day)}`;
};

// The six-week grid a month view draws, as rows of seven days.
export const monthWeeks = (anchor) => {
  const { from } = rangeFor('month', anchor);
  return Array.from({ length: 6 }, (_, w) => daysFrom(addDays(from, w * 7), 7));
};

// Group meetings by their date, so each cell or column looks up its own day once instead
// of filtering the whole window per cell.
export const byDate = (meetings) => {
  const map = new Map();
  for (const m of meetings) {
    const key = String(m.meeting_date).slice(0, 10);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(m);
  }
  return map;
};

// 'HH:MM' -> minutes, for positioning a meeting in the day and week grids. The server
// already sends the narrow form; this is deliberately not exported from a shared util
// because nothing else in the client has a time at all.
export const minutesOf = (time) => {
  const [h, m] = String(time).split(':').map(Number);
  return h * 60 + m;
};
